import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import hero2 from "../assets/cover1.webp";
import hero3 from "../assets/cover2.jpeg";
import hero4 from "../assets/cover3.jpg";
import hero5 from "../assets/cover4.jpg";
import hero6 from "../assets/cover5.webp";
import hero7 from "../assets/cover6.jpg";
import hero8 from "../assets/cover7.jpg";
import hero9 from "../assets/cover8.webp";
import hero10 from "../assets/cover9.jpg";

export const defaultHeroImages = [hero4, hero2, hero3, hero5, hero6, hero7, hero8, hero9, hero10];

export default function HeroSection() {
  const { settings } = useSettings();

  // Use admin-configured images if available, otherwise fall back to local assets
  const images = useMemo(() => {
    const heroImages = settings.hero_images;
    if (Array.isArray(heroImages) && heroImages.length > 0) {
      return heroImages;
    }
    return defaultHeroImages;
  }, [settings.hero_images]);

  const [current, setCurrent] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Trigger animations on mount
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Image carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  // Reset current index if images change and current is out of bounds
  useEffect(() => {
    if (current >= images.length) setCurrent(0);
  }, [images.length, current]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-slate-900 dark:via-slate-900 dark:to-emerald-950 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Text Content - Left Side */}
          <div
            className={`space-y-8 transform transition-all duration-1000 ${
              isVisible
                ? "translate-x-0 opacity-100"
                : "-translate-x-20 opacity-0"
            }`}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight">
              <span className="block text-slate-900 dark:text-white">
                Experience Morocco
              </span>
              <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500">
                Like Never Before
              </span>
            </h1>

            <p className="text-base md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl">
              Discover the magic of Morocco from the Sahara Desert to the Atlas Mountains.
              Authentic adventures await.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/tours"
                className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg shadow hover:bg-emerald-700 transform hover:-translate-y-0.5 transition-all duration-200"
              >
                Discover Tours
              </Link>

              <Link
                to="/contact"
                className="px-6 py-3 bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-semibold rounded-lg border-2 border-slate-300 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500 transition-all duration-200"
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* Image Carousel - Right Side */}
          <div
            className={`relative transform transition-all duration-1000 delay-300 ${
              isVisible
                ? "translate-x-0 opacity-100"
                : "translate-x-20 opacity-0"
            }`}
          >
            <div className="relative w-full h-[350px] md:h-[400px]">
              {images.map((img, i) => (
                <div
                  key={i}
                  className={`absolute inset-0 transition-opacity duration-1000 ${
                    i === current ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src={img}
                      alt={`Moroccan landscape ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                </div>
              ))}

              {/* Dots indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <div
                    key={i}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === current
                        ? "w-8 bg-emerald-500"
                        : "w-2 bg-white/50"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}
